"use client";

import { stegaClean } from "@sanity/client/stega";
import { resolveLinkUntracked } from "@/sanity/globals/CTA";
import { DEFAULT_LANGUAGE } from "@/sanity/globals/I18n";
import type { ITradingCompetitionLink } from "@/sanity/pages/PageTradingCompetitionTemplate";
import LanguageSwitcher from "@/components/global/partials/LanguageSwitcher";
import { useCtaHref } from "./ctaHref";

/** The site home in the page's language: "/", "/de", "/br". */
export function homeHref(language?: string) {
  const lang = stegaClean(language) || DEFAULT_LANGUAGE;
  return resolveLinkUntracked(lang === DEFAULT_LANGUAGE ? "/" : `/${lang}`, lang);
}

/** White bar: logo + language switcher + "Get funded". */
export default function TradingCompetitionNav({
  cta,
  language,
}: {
  cta?: ITradingCompetitionLink;
  language?: string;
}) {
  const ctaHref = useCtaHref(cta, language);
  // The switcher shows the German flag for any value it does not know, so a stega-encoded or empty language must not reach it.
  const lang = stegaClean(language) || DEFAULT_LANGUAGE;

  return (
    <header className="tc-intro-fade sticky top-0 z-50 w-full">
      <nav
        aria-label="Trading Competition"
        className="flex items-center justify-between border-b border-solid border-black/10 bg-white px-[2rem] py-[1.5rem] lg:px-[45rem] lg:pt-[1rem] lg:pb-[1.25rem]">
        <a href={homeHref(lang)} aria-label="IQ Capital home" className="flex shrink-0 items-center">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/trading-competition/logo.svg"
            alt="IQ Capital"
            className="h-[2.5171rem] w-[13.5905rem] lg:h-[3.1464rem] lg:w-[16.988rem]"
          />
        </a>

        <div className="flex items-center gap-[0.8rem]">
          {/* there is no Portuguese version of this page */}
          <LanguageSwitcher
            language={lang}
            languages={["en", "de"]}
          />
          {cta?.text && (
            <a
              href={ctaHref}
              className="flex h-[4.5rem] items-center justify-center rounded-[0.25rem] bg-black px-[2.25rem] font-geist text-[1.75rem] leading-[normal] font-semibold tracking-[-0.035rem] whitespace-nowrap text-white lg:h-[5rem]">
              {cta.text}
            </a>
          )}
        </div>
      </nav>
    </header>
  );
}
